import { snakeCase } from 'lodash';
import fetch from 'cross-fetch';
import { makeObservable, observable } from 'mobx';

import { NetworkError, createError } from 'core/lib/errors';
import { getLocale } from 'core/lib/localization';
import invariant from 'core/lib/invariant';
import { sleep } from 'utils/util';

import { env } from 'app/env';
import { createLogger } from 'app/logger';

import { Root } from '../models/root';
import { GlobalConfig } from '../models/global-config';
import { UserManager } from '../models/user-manager/user-manager';

const log = createLogger('api-invoker');

const createQueryString = (obj: any = null) => {
  if (!obj) return '';
  const string = Object.keys(obj)
    .map(key => {
      return `${snakeCase(key)}=${encodeURIComponent(obj[key])}`;
    })
    .join('&');

  return `?${string}`;
};

// must be true for endpoints which support cross-domain cookies
// (and are restricted the fixed list of origin hosts)
// must be false for endpoints which allow requests from any host (i.e. devtest)
const includeCredentialsEnabled = (targetApiEnv: string) => {
  const promiscuousApiEnvs = env.get('promiscuousApiEnvs') || [
    'devtest',
    'jfedev',
  ];
  return !promiscuousApiEnvs.includes(targetApiEnv);
};

const decorateFetchOptions = (options = {}) => {
  // so we can override the headers
  const {
    apiEnv,
    apiKey,
    appSlug,
    platform,
    manifestVersion,
    headers: optionHeaders = {},
    token,
    appInstallAttribution,
    ...restOfOptions
  }: any = options;

  invariant(apiKey, `apiKey configuration is required for API requests`);

  const locale = getLocale();

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    Authorization: `Bearer ${apiKey}`,
    // the normal User-Agent can't be overriden for web, so the rails
    // server uses X-User-Agent for the app/platform/version logic
    'X-User-Agent': `${appSlug}/${platform}/${manifestVersion}`,
    'Accept-Language': locale,
    ...optionHeaders,
  };

  if (token) {
    headers['X-User-Token'] = token;
  }

  if (appInstallAttribution) {
    headers['X-App-Install-Attribution'] = JSON.stringify(
      appInstallAttribution
    );
  }

  if (includeCredentialsEnabled(apiEnv)) {
    // credentials option needed for cross-site cookies
    return { headers, ...restOfOptions, credentials: 'include' };
  } else {
    // credentials option must be omitted when accessing devtest
    return { headers, ...restOfOptions };
  }
};

/**
 * Helper object to provide an abstraction to access the api
 * with baked-in loading flag.
 */
export class ApiInvoker {
  root: Root;

  @observable
  loadingData: boolean = false;

  @observable
  simulateNetworkFailure: boolean = false;

  constructor(root: Root) {
    this.root = root;
    makeObservable(this);
  }

  // /**
  //  * alias for window.fetch
  //  * @param  {...any} args
  //  */
  // async fetch(...args): Promise<Response> {
  //   // const { context = { addAction: () => {} } } = getEnv(self);

  //   // const root = getRoot(self);
  //   // const { simulateNetworkFailure = false } = root || {};
  //   if (!this.simulateNetworkFailure) {
  //     // // this is useful for testing purposes
  //     // const overrideEndpoint = env.get('overrideEndpoint', false);
  //     // if (overrideEndpoint) {
  //     //   args[0] = overrideEndpoint;
  //     // }

  //     // this was breaking the ts schema gen for some reason
  //     return window.fetch?.apply(null, args);
  //     // return fetch(...args);
  //   }

  //   // context.addAction({
  //   //   name: 'fetch-offline',
  //   //   path: 'ApiAccess',
  //   //   type: 'mobx',
  //   //   args,
  //   // });

  //   // temporal measure.
  //   // console.log('DOING NOTHING');
  //   return Promise.reject(new TypeError('Disconnected'));
  // }

  /**
   * make a GET request to the API
   * @param {*} endpoint
   * @param {*} query
   */
  get<T = any>(endpoint: string, query: object): Promise<T> {
    log.info(`apiGet ${endpoint}`);
    return this.api(endpoint, query, {});
  }

  /**
   * make a POST request to the API
   * @param {*} endpoint
   * @param {*} query
   */
  post<T = any>(
    endpoint: string,
    query: object,
    bodyData: object = null
  ): Promise<T> {
    log.info(`apiPost ${endpoint}`);
    const options: any = { method: 'POST' };

    if (bodyData) {
      options.body = JSON.stringify(bodyData);
      // console.log('BODY', options.body);
    }
    return this.api(endpoint, query, options);
  }

  put<T = any>(
    endpoint: string,
    query: object,
    bodyData: object = null
  ): Promise<T> {
    log.info(`apiPut ${endpoint}`);
    const options: any = { method: 'PUT' };

    if (bodyData) {
      options.body = JSON.stringify(bodyData);
    }
    return this.api(endpoint, query, options);
  }

  /**
   * utility function to make API requests
   *
   * It automatically starts and stop loading,
   * maybe we should add error catching here too.
   *
   * todo: support generically stuffing the 'X-User-Token' header
   */
  async api(
    endpoint: string,
    query: object = null,
    options: object
  ): Promise<any> {
    // console.log(`api: ${endpoint}`);
    const token = this.userManager.token || null;
    // marketing cookie data
    const appInstallAttribution = this.globalConfig.appInstallAttribution;
    // needed for dynamic CORS credentials handling
    const apiEnv = this.globalConfig.apiEnv;

    // get platform-related stuff
    const platformName = 'website'; //TODO platform.getPlatform();
    const apiKey = env.get(['jiveworldApiKey', platformName] as any, false);
    const manifestVersion = 'TODO'; // platform.getProductVersion();
    const appSlug = env.get('appSlug');

    const fetchOptions = decorateFetchOptions({
      ...options,
      apiEnv,
      apiKey,
      appSlug,
      platform: platformName,
      manifestVersion,
      token,
      appInstallAttribution,
    });

    try {
      if (query) {
        endpoint += createQueryString(query);
      }
      log.debug(
        `api: ${fetchOptions.method || 'GET'} ${this.baseUrl}${endpoint}`
      );
      this.startLoading(); // sets the loading flag on the root

      if (this.simulateNetworkFailure) {
        await sleep(1000);
        // todo: figure out what a real network error looks like
        throw new NetworkError('simulated network failure');
      }

      const url = `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, fetchOptions); // TODO: support disconnected mode testing

      // todo: any other statuses to let through?
      if (response.status && response.status >= 500) {
        throw new NetworkError(`Server error: ${response.status}`, {
          endpoint,
          query,
          fetchOptions,
        });
      }
      let data = null;
      try {
        data = await response.json();
        log.debug('data response', data);
      } catch (error) {
        log.warn(`json parse failure: ${error}`);
        // treat html response same as a 500 or other network failure
        throw new NetworkError(error as Error, {
          endpoint,
          query,
          fetchOptions,
        });
      }

      const { error, result } = data;
      if (error) {
        // throwing here triggers the catch below
        // the createError factory takes care of setting the correct one.
        throw error;
      }

      // expecting this to be handled by the UI layer moving forward
      //
      // // save the result of the last api call
      // // if it has a title, which means it will be used for message
      // if (result.title) {
      //   this.setApiResult(result);
      // }

      // if (result.messageKey) {
      //   // if the result has a messageKey, use it to display a flash
      //   this.root.setFlash(result);
      // }

      // console.log(`api - result: ${JSON.stringify(result)}`);
      return result;
    } catch (error) {
      log.warn('API ERROR', { error, endpoint, query, fetchOptions });
      if (error instanceof TypeError) {
        throw new NetworkError(error, { endpoint, query, fetchOptions });
      } else {
        throw createError(error);
      }
    } finally {
      this.stopLoading(); // removes the loading flag on the root
    }
  }

  startLoading(): void {
    this.loadingData = true;
  }

  stopLoading(): void {
    this.loadingData = false;
  }

  get globalConfig(): GlobalConfig {
    const root = this.root;
    if (root.globalConfig) {
      return root.globalConfig;
    } else {
      throw Error(`unexpectedly missing root.globalConfig`);
    }
  }

  get userManager(): UserManager {
    const root = this.root;
    if (root.userManager) {
      return root.userManager;
    } else {
      throw Error(`unexpectedly missing root.userManager`);
    }
  }

  get baseUrl() {
    return this.globalConfig.apiUrl;
  }
}
